import { Checkbox, Select, UserSelect } from '@/components_deprecated'
import { PlainLink } from '@/components_deprecated/Link'
import { IssueRiskAspectSelect } from '@/components_deprecated/Select/IssueRiskAspectSelect'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import type { UserNode } from '@/modules/users'
import type { ThumbnailUser } from '@/modules/users/UserThumbOrInitials'
import { format } from '@/utils/date-fns'
import { useModuleConfig, usePermissions } from '@/utils/hooks'
import { unlessLoadingDots } from '@/utils/loading'
import { PERMISSIONS } from '@/utils/permissions'
import { overloadColor } from '@/utils/style'
import type { SelectOption } from '@ur-solutions/react-components'
import type {
  FormErrors,
  FormOnChangeFn,
  UpdateFormFn,
} from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { set } from 'date-fns'
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIssueSeverityOptions } from '../consts'
import type { EditIssueForm } from '../types'
import type {
  IssueCategoryNode,
  IssueRiskAspectNode,
  IssueSeverity,
  LocalSubcategory,
} from '../types.graphl'
import type { ShallowCategory } from './CategorySelect'
import { CategorySelect } from './CategorySelect'
import { DatePicker, TimePicker } from '@/components'

const ResponsibleWrapper = styled.div`
  .--select-display-selected {
    color: ${props => props.theme.colors.black};
    display: flex;

    .--select-display-selected-clear {
      margin-top: auto;
      margin-bottom: auto;
      max-height: 20px;
    }
  }
`

interface WrapperProps {
  editing: boolean
}

const Wrapper = styled.section<WrapperProps>`
  grid-area: main;

  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-areas:
    '. . .'
    '. . .'
    '. date date';

  span.unknown {
    color: ${props => props.theme.colors.red};
  }

  ${props => props.theme.media.mobile} {
    overflow-x: auto;

    ${props =>
      props.editing &&
      css`
        display: flex;
        flex-direction: column;

        & > div {
          border: 1px solid ${props => props.theme.colors.gray8} !important;
          border-width: 1px 0 0 0 !important;

          &.category {
            order: -1;
            border-width: 0 !important;
          }

          &.date {
            order: -1;
          }
        }
      `};
  }
`
const Info = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  gap: 0.5rem;

  & + div {
    border-left: 1px solid ${props => props.theme.colors.gray8};

    &:nth-child(4) {
      border-left: 0;
    }

    &:nth-child(n + 4) {
      border-top: 1px solid ${props => props.theme.colors.gray8};
    }

    &:nth-child(7) {
      border-left: 0;
    }

    &:nth-child(8) {
      border-top: 1px solid ${props => props.theme.colors.gray8};
    }

    &:nth-child(9) {
      border-top: 1px solid ${props => props.theme.colors.gray8};
    }
  }

  font-weight: 600;

  & > span {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    color: ${props => props.theme.colors.gray5};
  }

  p {
    margin: 0;
    font-size: 1.6em;
  }

  ${props => props.theme.media.mobile} {
    padding: 1rem;

    span {
      font-size: 0.7rem;
    }

    p {
      font-size: 1rem;
    }
  }

  &.date {
    grid-area: date;
  }
`

interface IssueStatusProps {
  closed: boolean
  archived: boolean
}

const IssueStatus = styled.div<IssueStatusProps>`
  display: flex;
  align-items: center;

  &::after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 0.5ch;
    margin-bottom: 3px;

    border-radius: 50%;
    background: ${props =>
      props.archived
        ? props.theme.colors.orange
        : props.closed
        ? props.theme.colors.green
        : props.theme.colors.red};
  }
`

const Severity = styled.div<{ severity: IssueSeverity | null }>`
  display: flex;
  align-items: center;

  ${props =>
    props.severity !== null &&
    `
    &::after {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      margin-left: 0.5ch;
      margin-bottom: 3px;

      border-radius: 50%;
      background: ${
        props.severity === 'LOW'
          ? props.theme.colors.green
          : props.severity === 'MEDIUM'
          ? props.theme.colors.orange
          : props.theme.colors.red
      };
    }
  `}
`

interface IssueCategoryProps {
  color: string
}

const IssueCategory = styled.div<IssueCategoryProps>`
  &::after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 0.5ch;
    margin-bottom: 3px;

    border-radius: 50%;
    background: ${props => overloadColor(props.color)};
  }
`
const Editor = styled.div`
  margin-top: 0.5rem;
`
const DateEditor = styled(Editor)`
  display: flex;

  & > * + * {
    margin-left: 0.5rem;
  }
`
const TimeLabel = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.theme.colors.gray5};
`

const TimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

interface MainInfoProps {
  loading: boolean
  editing: boolean
  form: EditIssueForm
  errors: FormErrors<EditIssueForm>
  subCategory?: LocalSubcategory
  category:
    | (Pick<IssueCategoryNode, 'id' | 'name' | 'color'> & {
        responsibleUser: ThumbnailUser | null
      })
    | null
    | undefined
  date: string | undefined
  closedAt: string | null | undefined
  archivedAt: string | null | undefined
  reference: string | null | undefined
  user: ThumbnailUser | null | undefined
  responsible: ThumbnailUser | null | undefined
  severity: IssueSeverity | undefined
  riskAspect: IssueRiskAspectNode | null | undefined
  dateEnd: string | undefined

  onChange: FormOnChangeFn<EditIssueForm>
  updateForm: UpdateFormFn<EditIssueForm>
  subcategoryOptions: SelectOption<string, any>[]
  subcategoriesLoading: boolean
}

export const MainInfo: React.FC<MainInfoProps> = ({
  loading,
  editing,
  form,
  errors,
  subCategory,
  category,
  date,
  closedAt,
  archivedAt,
  reference,
  user,
  responsible,
  severity,
  riskAspect,
  dateEnd,

  onChange,
  updateForm,
  subcategoryOptions,
  subcategoriesLoading,
}) => {
  const translations = useTranslate({
    unknown: 'common.unknown',

    open: 'common.open-alt',
    closed: 'common.closed',
    archived: 'common.archived',

    status: 'common.status',
    subCategory: 'common.sub-category',
    category: 'common.category',
    reference: ['issues.reference', { abbreviate: false }],
    date: 'common.date',
    user: 'common.user',
    responsible: 'common.responsible',
    dateRange: 'common.date-range',

    riskAspect: 'common.risk-aspect',

    none: 'common.none',
    severity: 'common.severity',
    severityGrades: {
      LOW: 'common.low',
      MEDIUM: 'common.medium',
      HIGH: 'common.high',
    },
    dataFrom: 'common.data-from',
    dataTo: 'common.data-to',
  })

  const severityOptions = useIssueSeverityOptions()

  const [temporaryResponsible, setTemporaryResponsible] = useState<Pick<
    UserNode,
    'id' | 'fullName'
  > | null>(null)

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(PERMISSIONS.issues.change.issue)
  const infringementModuleConfig = useModuleConfig(Module.INFRINGEMENTS)
  const userCanCloseOwnInfringement =
    infringementModuleConfig.getModuleOptionStatus(
      ModuleOptionsValue.INFRINGEMENTS_USER_CAN_CLOSE_OWN_INFRINGEMENT
    )

  const handleSetTemporaryResponsible = useEffectEvent(
    (user: Pick<UserNode, 'id' | 'fullName'> | null) => {
      setTemporaryResponsible(user)
    }
  )

  useEffect(() => {
    handleSetTemporaryResponsible(editing ? responsible ?? null : null)
  }, [editing, responsible])

  function handleChangeCategory(id: string, category: ShallowCategory | null) {
    updateForm({
      category: id,
      severity: category?.defaultSeverity ?? form.severity,
    })
    setTemporaryResponsible(category?.responsibleUser ?? temporaryResponsible)
  }

  function handleChangeDate(date: Date, fieldName: keyof EditIssueForm) {
    const baseDate =
      form[fieldName] instanceof Date ? form[fieldName] : new Date()

    const newDate = set(date, {
      hours: baseDate.getHours(),
      minutes: baseDate.getMinutes(),
      seconds: baseDate.getSeconds(),
    })

    if (fieldName === 'date' && form.dateEnd && newDate > form.dateEnd) {
      onChange('dateEnd')(newDate)
    }

    onChange(fieldName)(newDate)
  }

  const unlessLoading = unlessLoadingDots(loading, translations.unknown)

  const issueStatus = useMemo(() => {
    if (typeof closedAt === 'undefined' && typeof archivedAt === 'undefined')
      return null

    const archived =
      archivedAt !== null ? ` (${translations.archived.toLowerCase()})` : ''
    let text = ''

    if (closedAt !== null) text = `${translations.closed}${archived}`
    else text = `${translations.open}${archived}`

    return (
      <IssueStatus closed={closedAt !== null} archived={archivedAt !== null}>
        {text}
      </IssueStatus>
    )
  }, [
    archivedAt,
    closedAt,
    translations.archived,
    translations.closed,
    translations.open,
  ])

  const issueCategory = useMemo(() => {
    if (!category) return '-'

    return <IssueCategory color={category.color}>{category.name}</IssueCategory>
  }, [category])

  const displaySeverity = severity ?? form.severity

  useEffect(() => {
    if (subcategoryOptions?.length > 0 || subcategoriesLoading) return

    if (form.subcategory !== null) {
      onChange('subcategory')(null)
    }
  }, [
    form.category,
    subcategoryOptions,
    subcategoriesLoading,
    form.subcategory,
    onChange,
  ])

  return (
    <Wrapper editing={editing}>
      <Info>
        <span>{translations.status}</span>
        {!editing ||
        (!userCanCloseOwnInfringement && form.forInfringements && !canEdit) ? (
          <p>{unlessLoading(issueStatus)}</p>
        ) : (
          <Editor>
            <Checkbox
              checked={form.closed}
              label={translations.closed}
              boxSize="2rem"
              onChange={onChange('closed')}
            />
          </Editor>
        )}
      </Info>

      <Info>
        <span>{translations.user}</span>
        <p>
          {unlessLoading(
            <PlainLink
              to={user ? `/users/${user?.id}` : '#'}
              $hoverColor="secondary"
            >
              {user?.fullName ?? '-'}
            </PlainLink>
          )}
        </p>
      </Info>

      <Info>
        <span>{translations.reference()}</span>
        <p>{unlessLoading(reference)}</p>
      </Info>

      <Info>
        <span>{translations.severity}</span>
        {!editing || !canEdit ? (
          <p>
            <Severity severity={displaySeverity}>
              {unlessLoading(
                displaySeverity
                  ? translations.severityGrades[displaySeverity]
                  : '-'
              )}
            </Severity>
          </p>
        ) : (
          <Editor>
            <Select
              value={form.severity}
              options={severityOptions}
              onChange={value => onChange('severity')(value)}
              height="3rem"
              grayed
              fullWidth
              nullable
              error={errors.severity}
            />
          </Editor>
        )}
      </Info>

      <Info>
        <span>{translations.responsible}</span>
        {!editing || !canEdit ? (
          <p>
            {unlessLoading(
              <PlainLink
                to={responsible ? `/users/${responsible?.id}` : '#'}
                $hoverColor="secondary"
              >
                {responsible?.fullName ?? '-'}
              </PlainLink>
            )}
          </p>
        ) : canEdit ? (
          <ResponsibleWrapper>
            <Editor>
              <UserSelect
                value={temporaryResponsible?.id ?? null}
                dropdownPageName="issue_category_responsible"
                height="3rem"
                onChange={(value, option) => {
                  onChange('responsibleUser')(value)
                  if (value === null)
                    setTemporaryResponsible(category?.responsibleUser ?? null)
                  else setTemporaryResponsible(option?.extra ?? null)
                }}
                fullWidth
                nullable={
                  temporaryResponsible?.id !== category?.responsibleUser?.id
                }
                grayed
              />
            </Editor>
          </ResponsibleWrapper>
        ) : (
          <p>
            {temporaryResponsible ? (
              <PlainLink
                to={`/users/${temporaryResponsible.id}`}
                $hoverColor="secondary"
              >
                {temporaryResponsible.fullName}
              </PlainLink>
            ) : (
              translations.none
            )}
          </p>
        )}
      </Info>

      <Info className="category">
        <span>{translations.category}</span>
        {!editing || !canEdit || form.forInfringements ? (
          <p>{unlessLoading(issueCategory)}</p>
        ) : (
          <Editor>
            <CategorySelect
              value={form.category}
              fullWidth
              onChange={handleChangeCategory}
            />
          </Editor>
        )}
        {!editing || !canEdit || form.forInfringements
          ? subCategory?.name && (
              <>
                <span>{translations.subCategory}</span>
                <p>{unlessLoading(subCategory?.name)}</p>
              </>
            )
          : form.category &&
            subcategoryOptions?.length > 0 && (
              <>
                <span>{translations.subCategory}</span>
                <Select
                  loading={subcategoriesLoading}
                  value={form.subcategory}
                  options={subcategoryOptions}
                  onChange={onChange('subcategory')}
                  height="3rem"
                  grayed
                  fullWidth
                  nullable
                  error={errors.subcategory}
                />
              </>
            )}
      </Info>

      <Info>
        <span>{translations.riskAspect}</span>
        {!editing || !canEdit ? (
          <p>{unlessLoading(riskAspect?.name ?? '-')}</p>
        ) : (
          <Editor>
            <IssueRiskAspectSelect
              value={form.riskAspect}
              onChange={value => onChange('riskAspect')(value)}
              height="3rem"
              fullWidth
              nullable
              grayed
              error={errors.riskAspect}
            />
          </Editor>
        )}
      </Info>

      <Info
        className="date"
        title={date ? format(new Date(date), 'dd.MM.yyyy HH:mm') : undefined}
      >
        {!editing || form.forInfringements ? (
          <>
            <span>{translations.date}</span>
            <p>
              {unlessLoading(format(new Date(date ?? 0), 'PP, HH:mm'))}-
              {unlessLoading(format(new Date(dateEnd ?? 0), 'PP, HH:mm'))}
            </p>
          </>
        ) : (
          <TimeContainer>
            <div>
              <TimeLabel>{translations.dataFrom}</TimeLabel>
              <DateEditor>
                <TimePicker value={form.date} onChange={onChange('date')} />
                <DatePicker
                  mode="single"
                  value={form.date}
                  onChange={date =>
                    date ? handleChangeDate(date, 'date') : undefined
                  }
                />
              </DateEditor>
            </div>
            <div>
              <TimeLabel>{translations.dataTo}</TimeLabel>
              <DateEditor>
                <TimePicker
                  value={form.dateEnd}
                  onChange={onChange('dateEnd')}
                />
                <DatePicker
                  mode="single"
                  value={form.dateEnd}
                  onChange={date =>
                    date ? handleChangeDate(date, 'dateEnd') : undefined
                  }
                />
              </DateEditor>
            </div>
          </TimeContainer>
        )}
      </Info>
    </Wrapper>
  )
}
